import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IItemCreationSubsectionInputProps,
  ItemCreationSubsection,
  TextEditor,
} from '~/Components';
import { AtSign, User } from '~/Components/Icons';
import { ItemCreationValidInput } from '~/Components/ItemCreation/ItemCreationSubsection/ItemCreationSubsectionInput.model';
import { makeStyles } from '~/UIKit/Style';
import { ExtendedStaff } from '../ExtendedSaff.model';

import { PhoneSimple } from '~/Components/Icons/PhoneSimple';
import { useProvider } from '~/Hooks';
import { StaffEditCustomImage } from '../StaffEditCustomElements';
import { StaffRoleSelect } from '../StaffEditCustomElements/StaffRoleSelect';

const useStyles = makeStyles(() => ({
  container: {
    paddingBottom: 30,
  },
}));

export const StaffEditSection = (staff: ExtendedStaff, hasRoles: boolean) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const boundariesElement = useRef(null);
  const { isUsingThePartnerTool } = useProvider();
  const StaffImage: IItemCreationSubsectionInputProps<Partial<ExtendedStaff>>[] = [
    {
      customElement: <StaffEditCustomImage />,
    },
  ];

  const StaffPersonalInformation: IItemCreationSubsectionInputProps<Partial<ExtendedStaff>>[] = [
    {
      icon: <User />,
      label: `${t('app.firstName')}`,
      placeholder: t('app.firstName'),
      type: ItemCreationValidInput.TEXT,
      name: 'firstName',
      hasAnError: staff?.firstName?.length === 0,
      InputProps: { inputProps: { maxLength: 50 } },
      noIconBackground: true,
      disabled: hasRoles && !!staff.accessType && staff.accessType > 0,
    },
    {
      icon: <User />,
      label: `${t('app.lastName')}`,
      placeholder: t('app.lastName'),
      type: ItemCreationValidInput.TEXT,
      name: 'lastName',
      hasAnError: staff?.lastName?.length === 0,
      InputProps: { inputProps: { maxLength: 50 } },
      noIconBackground: true,
      disabled: hasRoles && !!staff.accessType && staff.accessType > 0,
    },
    {
      icon: <AtSign />,
      label: `${t('app.email')}`,
      placeholder: t('app.email'),
      type: ItemCreationValidInput.TEXT,
      name: 'email',
      hasAnError: !!staff.accessType && staff?.accessType > 0 && staff?.email?.length === 0,
      InputProps: { inputProps: { maxLength: 50 } },
      noIconBackground: true,
      disabled: hasRoles && !!staff.accessType && staff.accessType > 0,
    },
    {
      icon: <PhoneSimple />,
      label: `${t('app.phone')}`,
      placeholder: t('app.phone'),
      type: ItemCreationValidInput.TEXT,
      name: 'phone',
      hasAnError: false,
      InputProps: { inputProps: { maxLength: 50 } },
      noIconBackground: true,
    },
  ];

  const StaffAccess: IItemCreationSubsectionInputProps<Partial<ExtendedStaff>>[] = [
    {
      customElement: <StaffRoleSelect isOwner={staff.isOwner} />,
    },
  ];

  const DescriptionSectionInputs: IItemCreationSubsectionInputProps<Partial<ExtendedStaff>>[] = [
    {
      customElement: (
        <TextEditor
          title={t('form.field.description')}
          fieldName="descriptions"
          valuesAsArray
          tooltip={{
            title: t('form.field.staff.description.hint'),
          }}
        />
      ),
      customElementFullSection: true,
    },
  ];

  return (
    <div ref={boundariesElement} className={classes.container}>
      <ItemCreationSubsection items={StaffImage} />
      <ItemCreationSubsection
        items={StaffPersonalInformation}
        title={t('settings.staff.personalInformation')}
        isHeaderVisible
      />
      <ItemCreationSubsection
        tooltip={{
          title: isUsingThePartnerTool
            ? t('settings.staff.access.partner.tooltip')
            : t('settings.staff.access.provider.tooltip'),
          boundariesElement: boundariesElement.current,
        }}
        items={StaffAccess}
        title={t('app.access')}
        isHeaderVisible
      />
      <ItemCreationSubsection items={DescriptionSectionInputs} />
    </div>
  );
};
